// Branchenvorlage ansehen (grosse Vorschau) und verwenden (eigene Kopie, optional als Folie in einer Playlist).
import type { LayoutV2 } from '@disploraq/layouts';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { can, qk, useMe, usePlaylists } from '../../api/queries';
import type { LayoutRow, TemplateCategory } from '../../api/types';
import { Icon } from '../../components/Icon';
import { LayoutPreview, type MediaIndex } from '../../components/Preview';
import { useToast } from '../../components/toast';
import { Dialog, Field } from '../../components/ui';
import { previewPath } from '../../preview/open';
import { sampleWeather, tr, type TemplateGroup } from './shared';
import { EditCopyButton } from '../template-edit/TemplateEdit';

type Orientation = 'landscape' | 'portrait';

export function TemplateFrame({ layout, media, live = false, orientation }: { layout: LayoutRow | undefined; media: MediaIndex; live?: boolean; orientation: Orientation }) {
  return (
    <div className={`tg-frame ${orientation}`}>
      {layout && <LayoutPreview definition={layout.definition} media={media} live={live} weather={sampleWeather} />}
    </div>
  );
}

/** Format-Umschalter 16:9 / 9:16 einer Vorlagengruppe. */
export function FormatSwitch({ group, value, onChange, small }: { group: TemplateGroup; value: Orientation; onChange: (o: Orientation) => void; small?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={`seg${small ? ' sm' : ''}`} role="group" aria-label={t('templates.format')}>
      {(['landscape', 'portrait'] as const).filter((o) => group.variants[o]).map((o) => (
        <button key={o} type="button" className={value === o ? 'on' : ''} aria-pressed={value === o}
          onClick={(e) => { e.stopPropagation(); onChange(o); }}>{o === 'portrait' ? '9:16' : '16:9'}</button>
      ))}
    </div>
  );
}

export function TemplateDetailDialog({ group, category, layouts, media, initial, onClose, onUse }: {
  group: TemplateGroup; category: TemplateCategory | undefined; layouts: LayoutRow[]; media: MediaIndex; initial: Orientation;
  onClose: () => void; onUse: (o: Orientation) => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const [o, setO] = useState<Orientation>(group.variants[initial] ? initial : group.variants.landscape ? 'landscape' : 'portrait');
  const item = group.variants[o]!;
  const layout = layouts.find((l) => l.id === item.layoutId);
  const fields = (layout?.definition as LayoutV2 | undefined)?.fields ?? [];
  const lang = i18n.language;
  return (
    <Dialog wide title={tr(group.name, lang)} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.close')}</button>
      <a className="btn" href={previewPath('layout', item.layoutId)} target="_blank" rel="noopener" title={t('preview.openHint')}><Icon name="play" />{t('preview.open')}</a>
      {can(me, 'layouts.manage') && <button className="btn primary" onClick={() => onUse(o)}><Icon name="check" />{t('gallery.use')}</button>}
      <EditCopyButton layout={{ id: item.layoutId, name: tr(group.name, lang) }} />
    </>}>
      <div className={`tg-detail ${o}`}>
        <div className="tg-stage"><TemplateFrame layout={layout} media={media} live orientation={o} /></div>
        <div className="col" style={{ alignContent: 'start' }}>
          {category && <span className="tg-cat" style={{ ['--c' as string]: category.color }}>{tr(category.name, lang)}</span>}
          <div className="muted">{tr(group.description, lang)}</div>
          <FormatSwitch group={group} value={o} onChange={setO} />
          <div>
            <div className="lbl small muted" style={{ marginBottom: 6 }}>{t('gallery.fields', { count: fields.length })}</div>
            <div className="tg-fields">{fields.map((f) => <span key={f.zone} className="pill neutral plain">{tr(f.label, lang)}{f.required ? ' *' : ''}</span>)}</div>
          </div>
          <div className="hint small">{t('gallery.detailHint')} {t('tedit.detailHint')}</div>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * „Vorlage verwenden": legt eine eigene Kopie an. Als Folie in einer Playlist geht es danach ins Formular (M16),
 * sonst ins Studio.
 */
export function UseTemplateDialog({ group, orientation, onClose }: { group: TemplateGroup; orientation: Orientation; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const { data: playlists } = usePlaylists();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const canPlaylist = can(me, 'playlists.manage');
  const item = group.variants[orientation] ?? group.variants.landscape ?? group.variants.portrait!;
  const [name, setName] = useState(tr(group.name, i18n.language) + (item.orientation === 'portrait' ? ` (${t('gallery.portraitShort')})` : ''));
  const [mode, setMode] = useState<'playlist' | 'studio'>(canPlaylist ? 'playlist' : 'studio');
  const [target, setTarget] = useState<string>('new');
  const [newName, setNewName] = useState(tr(group.name, i18n.language));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const body: Record<string, string> = { name: name.trim() };
      if (mode === 'playlist') {
        if (target === 'new') body.newPlaylistName = newName.trim();
        else body.playlistId = target;
      }
      const res = await api.post<{ layout: LayoutRow; playlistId: string | null; itemId: string | null }>(`/templates/${item.key}/use`, body);
      await Promise.all([qc.invalidateQueries({ queryKey: qk.layouts }), qc.invalidateQueries({ queryKey: qk.playlists })]);
      toast.show(t('gallery.used', { name: res.layout.name }));
      if (res.playlistId && res.itemId) navigate(`/playlists/${res.playlistId}/items/${res.itemId}/design`);
      else navigate(`/studio/${res.layout.id}`);
    } catch (e) {
      toast.error(e);
      setBusy(false);
    }
  };
  const valid = !!name.trim() && (mode === 'studio' || target !== 'new' || !!newName.trim());

  return (
    <Dialog title={t('gallery.useTitle', { name: tr(group.name, i18n.language) })} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={!valid || busy} onClick={() => void submit()}><Icon name="check" />{t('gallery.use')}</button>
    </>}>
      <form className="col" onSubmit={(e) => { e.preventDefault(); if (valid) void submit(); }}>
        <div className="hint small">{t('gallery.copyHint')}</div>
        <Field label={t('gallery.copyName')}><input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} /></Field>
        <div className="col" role="radiogroup" aria-label={t('gallery.whatNext')} style={{ gap: 8 }}>
          {canPlaylist && (
            <label className={`tg-choice${mode === 'playlist' ? ' on' : ''}`}>
              <input type="radio" name="mode" checked={mode === 'playlist'} onChange={() => setMode('playlist')} />
              <span><b>{t('gallery.asSlide')}</b><br /><span className="muted small">{t('gallery.asSlideHint')}</span></span>
            </label>
          )}
          {mode === 'playlist' && canPlaylist && (
            <div className="tg-two" style={{ paddingLeft: 28 }}>
              <Field label={t('gallery.playlist')}>
                <select value={target} onChange={(e) => setTarget(e.target.value)}>
                  <option value="new">{t('gallery.newPlaylist')}</option>
                  {(playlists ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
              {target === 'new' && <Field label={t('gallery.newPlaylistName')}><input value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={200} /></Field>}
            </div>
          )}
          <label className={`tg-choice${mode === 'studio' ? ' on' : ''}`}>
            <input type="radio" name="mode" checked={mode === 'studio'} onChange={() => setMode('studio')} />
            <span><b>{t('gallery.asTemplate')}</b><br /><span className="muted small">{t('gallery.asTemplateHint')}</span></span>
          </label>
        </div>
      </form>
    </Dialog>
  );
}
